
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from "@angular/forms";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { FormService } from "../../services/form.service";
import { PossibleMatchesComponent } from "../../form-parts/possible-matches/possible-matches.component";
import { ApiService } from "../../services/api.service";
import { first } from "rxjs";
import {
	betterEmailValidator,
	betterFullNameValidator,
	betterPhoneNumberValidator
} from "../../services/custom-validators";
import { completePhoneNumber, intlCountryOptions } from "../../services/phone-number";

// Tell editor this exists
declare var intlTelInput: any;

@Component( {
	selector: 'app-name-email-phone',
	templateUrl: './name-email-phone.component.html',
	styleUrls: ['./name-email-phone.component.scss']
} )
export class NameEmailPhoneComponent implements OnInit {

	nameEmailPhone !: UntypedFormGroup
	formGroupName: string = 'nameEmailPhone'
	@ViewChild( 'intlInputEl' ) intlInputEl!: ElementRef;
	public intlInstance: any;
	public intlLoaded = false;
	fullNameValidatorAdded = false;
	emailValidatorAdded = false;
	emailBounceDidYouMean = '';
	numberValidatorAdded = false;
	validatorState = { isValidating: false };

	constructor(
		private dialog: MatDialog,
		public formService: FormService,
		private apiService: ApiService
	) {
	}

	ngOnInit(): void {

		this.nameEmailPhone = this.formService.mainForm.get( this.formGroupName ) as UntypedFormGroup;

		// Sync inputs
		this.nameEmailPhone.controls['phoneNumber'].valueChanges.subscribe( x => {

			if( typeof this.intlInstance === 'undefined' ) {

				console.log( 'IntlInstance was undefined' );
				this.loadIntlTelInput();
			}
		} );

		// Subscribe to the step change system
		this.formService.stepChange$.subscribe( currentFormGroupName => {

			// If the step name is movingSize
			if( currentFormGroupName === 'movingSize' ) {

				// I chose moving size because that's the step when we know that
				// the movingFrom and movingTo details are filled in.
				// The phone number field will use the countryFrom and countryTo

				// If it's not loaded already
				if( ! this.intlLoaded ) {

					console.log( 'Got ordered to load intl Input' );
					this.loadIntlTelInput()
				}
			}
		} );
	}

	ngAfterViewInit() {

		// After view has loaded
	}

	/**
	 * This is added after the first focusout because we don't want the error to popup while the user is still typing
	 * @param event
	 */
	public verifyName( event: any ) {

		// Get value
		if( event.target.value.length > 0 && ! this.fullNameValidatorAdded ) {

			var nameField = this.nameEmailPhone.get( 'name' );

			nameField?.addValidators( betterFullNameValidator() );
			nameField?.updateValueAndValidity();

			// Make sure we only add the validator once
			this.fullNameValidatorAdded = true;
		}
	}

	public loadIntlTelInput() {

		// scripts.js (which carries intlTelInput) failed to load, or the view is not there yet
		if( typeof intlTelInput !== 'function' || ! this.intlInputEl?.nativeElement ) {

			return;
		}

		var countryFrom = this.formService.verifyCountryCode( (<UntypedFormGroup>this.formService.mainForm.get( 'movingFrom' )).controls['country'].value );
		var countryTo = this.formService.verifyCountryCode( (<UntypedFormGroup>this.formService.mainForm.get( 'movingTo' )).controls['country'].value );

		// Initiate the intlTelInput
		this.intlInstance = intlTelInput( this.intlInputEl.nativeElement, {
			// Options here
			...intlCountryOptions( countryFrom, countryTo ),
			utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.16/js/utils.min.js',
		} );

		this.intlLoaded = true;

		console.log( 'Intl Instance should be available now' );

		// When country flag changes
		this.intlInputEl.nativeElement.addEventListener("countrychange", () => {

			// Guard: if the validator itself triggered setNumber (which fires this event),
			// skip re-triggering validation to prevent an infinite loop
			if( this.validatorState.isValidating ) {
				return;
			}

			// Call validator
			const phoneNumberControl = this.nameEmailPhone.controls['phoneNumber'];
			phoneNumberControl.updateValueAndValidity();
		} );
	}

	public formatCompleteNumber() {

		// E164 when intl-tel-input is usable, otherwise the number as typed
		var number = completePhoneNumber( this.intlInstance, this.nameEmailPhone.controls['phoneNumber'].value );
		this.nameEmailPhone.controls['phoneNumberComplete'].setValue( number );
	}

	// On blur of phone number field
	public verifyPhoneNumber( event: any ) {

		// There has to be a value at least
		if( event.target.value.length > 0 ) {

			if( ! this.numberValidatorAdded ) {

				const phoneNumberControl = this.nameEmailPhone.controls['phoneNumber'];

				console.info( 'Adding validator for phone numbers' );
				phoneNumberControl?.addValidators( betterPhoneNumberValidator(() => this.intlInstance, this.validatorState) );
				phoneNumberControl?.updateValueAndValidity();

				this.numberValidatorAdded = true;
			}
		}
	}

	// Only allow space, +, (), - and numbers
	public onPhoneNumberInput( event: any ) {

		// Remove any non-numeric characters except for the '+'
		const input = event.target;
		input.value = input.value.replace(/[^0-9\s\-()+]/g, '');
	}

	public verifyEmail( event: any ) {

		// There has to be a value at least
		if( event.target.value.length > 0 ) {

			const emailControl = this.nameEmailPhone.controls['email'];

			// The regex validator for email isn't added yet
			if(  ! this.emailValidatorAdded ) {

				emailControl?.addValidators( betterEmailValidator() );
				emailControl?.updateValueAndValidity();

				// Make sure we only add the validator once
				this.emailValidatorAdded = true;
			}

			// Check if it passes the betterEmailValidator()
			// If it doesn't, we do not have to do the zeroBounce request
			if( emailControl.valid ) {

				// Check for email status with ZeroBounce API
				this.emailBounceCheck( event.target.value );
			}
		}
	}

	private emailBounceCheck( emailValue: string ) {

		// Check with ZeroBounce to see if this is a valid email
		this.apiService.getZeroBounceResult( emailValue ).pipe( first() ).subscribe( {

			next: response => {

				interface ResponseType {
					status: string;
					did_you_mean?: string;
				}
				const buildError = ( response: ResponseType ) => {

					this.nameEmailPhone.controls['email'].setErrors({ zeroBounceInvalid: true } );
					this.emailBounceDidYouMean = response.did_you_mean ? response.did_you_mean : '';
				}
					
				// Fail-open on API error payload
				if( typeof response?.error === 'string' ) {
					this.nameEmailPhone.controls['email'].setErrors(null);
					this.emailBounceDidYouMean = '';
					return;
				}

				// If response is missing expected fields, fail-open
				if( typeof response?.status !== 'string' ) {
					this.nameEmailPhone.controls['email'].setErrors(null);
					this.emailBounceDidYouMean = '';
					return;
				}

				if( response.status === 'invalid' ) {

					buildError(response);
				}
				else {
					this.nameEmailPhone.controls['email'].setErrors(null);
					this.emailBounceDidYouMean = '';
				}
			},
			error: error => {

				console.log( error )
			}
		} );
	}

	public useDidYouMeanEmail(event: Event) {

		// Do not add the #
		event.preventDefault();

		// Overwrite the current value with the suggested value from zero bounce
		this.nameEmailPhone.controls['email'].setValue( this.emailBounceDidYouMean );

		// Verify email on the did you mean version
		this.emailBounceCheck( this.emailBounceDidYouMean );
	}

	public onSubmit() {

		// Build up complete number
		this.formatCompleteNumber();

		// On this step we set the _submitted of this formGroup only on true if the whole form is valid
		this.formService.submitFormGroup( (<UntypedFormGroup>this.nameEmailPhone) );
		this.formService.submitMainForm();
	}

	public getPrivacyPolicyUrl() {

		const url = this.formService.domainData?.privacyPolicyUrl;
		return ( typeof url === 'string' && url.length > 0 ? url : '#' );
	}

	public getPossibleMatchesPopup() {

		var origin = (<UntypedFormGroup>this.formService.mainForm.get( 'movingFrom' )).controls['country'].value;
		var destination = (<UntypedFormGroup>this.formService.mainForm.get( 'movingTo' )).controls['country'].value;

		var body = {
			origin: this.formService.verifyCountryCode( origin ),
			destination: this.formService.verifyCountryCode( destination ),
		}

		this.apiService.getPossibleMatches( body ).subscribe( response => {

			const dialogConfig = new MatDialogConfig();

			// Pass data
			dialogConfig.data = {
				movers: response.moving_companies,
				service_providers: response.service_providers
			};

			this.dialog.open( PossibleMatchesComponent, dialogConfig );
		} );
	}
}